import { useRef, useState } from "react";

import { MessageCircle, Send, Sparkles, X } from "lucide-react";

import { Button, Textarea } from "@rook/ui";

import { sendChatMessage, type ChatMessage } from "./api";
import { sendMockChatMessage } from "./api.mock";
import { getAiChatConfig } from "./config";
import { useAuth } from "../../auth/AuthProvider";
import { isDesignShowcaseUnlocked } from "../../lib/designPreview";

/**
 * Floating AI chat widget (bottom-right) — ROOK's AI replacement for the
 * legacy Intercom bubble.
 *
 * Per CPF-NFR-033 (control-plane / data-plane) it ships in every app and
 * talks to ONE control-plane chat backend over HTTPS; it's env-agnostic,
 * so the same widget works whether the customer is in the prod or sandbox
 * portal (no cross-account anything — it's an authenticated HTTPS call).
 *
 * Two gates, both must pass to render:
 *   1. Build flag (`getAiChatConfig().enabled`) — off by default until the
 *      backend is live + `VITE_AI_CHAT_*` is set. Wiring it into a shell
 *      now is a no-op.
 *   2. ENTERPRISE entitlement — the AI assistant is a paid/prod feature, so
 *      it only appears once the client has PRODUCTION activated
 *      (`session.isProductionActive`). (Pepe 2026-06-04.)
 *
 * NOTE: the PORTAL's own session probe (P-030, see session.ts) does NOT yet
 * project `isProductionActive`, so today the gate keeps the widget hidden in
 * the portal even with the flag on — correct while the backend is also off.
 * Projecting that field on the portal session is the prerequisite to light
 * this up (tracked alongside the AI-chat backend work).
 *
 * Position: bottom-RIGHT (where Intercom lived) — the onboarding checklist
 * is bottom-LEFT, so the two never fight for the same corner.
 */
export const AiChatWidget = () => {
  const config = getAiChatConfig();
  const { enabled, apiUrl } = config;
  const { session } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const listEndRef = useRef<HTMLDivElement>(null);

  // Hooks must run unconditionally (rules-of-hooks); gate AFTER them.
  // Showcase mode bypasses the backend + entitlement gates: it renders the
  // widget against the canned mock sender. Activated by the build flag OR
  // the ?designShowcase unlock (so customers on a normal build never see
  // it). Otherwise the enterprise gate applies: production must be active.
  const showcase = config.showcase || isDesignShowcaseUnlocked();
  const prodActive = session.ok && session.isProductionActive === true;
  if (!showcase && (!enabled || !prodActive)) return null;

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    const next: ChatMessage[] = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setError(null);
    setSending(true);
    try {
      const reply = showcase
        ? await sendMockChatMessage(next)
        : await sendChatMessage(apiUrl, next);
      setMessages((m) => [
        ...m,
        { role: "assistant", content: reply || "…" },
      ]);
    } catch {
      setError("Couldn't reach ROOK AI. Try again in a moment.");
    } finally {
      setSending(false);
      requestAnimationFrame(() =>
        listEndRef.current?.scrollIntoView({ behavior: "smooth" }),
      );
    }
  };

  return (
    <>
      {open && (
        <section
          aria-label="ROOK AI assistant"
          className="fixed bottom-24 right-5 z-50 flex h-[28rem] w-[22rem] max-w-[calc(100vw-2.5rem)] flex-col overflow-hidden rounded-2xl border border-rook-active bg-rook-back shadow-xl"
        >
          <header className="flex items-center justify-between border-b border-rook-active px-4 py-3">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-rook-purple" aria-hidden />
              <span className="text-sm font-semibold text-rook-stone">
                ROOK AI
              </span>
            </div>
            <Button
              type="button"
              variant={null}
              size={null}
              onClick={() => setOpen(false)}
              aria-label="Close chat"
              className="rounded-md p-1 text-rook-olive hover:bg-rook-home"
            >
              <X className="h-4 w-4" />
            </Button>
          </header>

          <div className="flex-1 space-y-3 overflow-y-auto px-4 py-3">
            {messages.length === 0 && (
              <p className="text-sm text-rook-olive">
                Ask me anything about integrating ROOK — webhooks, the SDK,
                data structure, providers…
              </p>
            )}
            {messages.map((m, i) => (
              <div
                key={`${i}-${m.role}`}
                className={m.role === "user" ? "text-right" : "text-left"}
              >
                <span
                  className={
                    m.role === "user"
                      ? "inline-block max-w-[85%] whitespace-pre-wrap rounded-2xl bg-rook-purple px-3 py-2 text-left text-sm text-white"
                      : "inline-block max-w-[85%] whitespace-pre-wrap rounded-2xl bg-rook-home px-3 py-2 text-sm text-rook-stone"
                  }
                >
                  {m.content}
                </span>
              </div>
            ))}
            {sending && (
              <p className="text-sm text-rook-olive">
                ROOK AI is thinking…
              </p>
            )}
            {error && <p className="text-sm text-rook-danger">{error}</p>}
            <div ref={listEndRef} />
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              void send();
            }}
            className="flex items-end gap-2 border-t border-rook-active p-3"
          >
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void send();
                }
              }}
              rows={1}
              placeholder="Ask ROOK AI…"
              aria-label="Message"
              className="max-h-28 flex-1 resize-none"
            />
            <Button
              type="submit"
              variant={null}
              size={null}
              disabled={sending || input.trim() === ""}
              aria-label="Send"
              className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-rook-purple text-white transition hover:opacity-90 disabled:opacity-40"
            >
              <Send className="h-4 w-4" />
            </Button>
          </form>
        </section>
      )}

      <Button
        type="button"
        variant={null}
        size={null}
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? "Close ROOK AI" : "Open ROOK AI"}
        aria-expanded={open}
        className="fixed bottom-5 right-5 z-50 grid h-12 w-12 place-items-center rounded-full bg-rook-purple text-white shadow-lg transition hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-rook-purple"
      >
        {open ? (
          <X className="h-5 w-5" />
        ) : (
          <MessageCircle className="h-5 w-5" />
        )}
      </Button>
    </>
  );
};
